import { useMediaQuery, useTheme } from "@mui/material";
import { useMemo, useEffect, useState } from "react";

type DeviceType = "phone" | "tablet" | "desktop";
type Orientation = "portrait" | "landscape";

interface DeviceInfo {
  type: DeviceType;
  orientation: Orientation;
}

const useDeviceType = (): DeviceInfo => {
  const isDesktop = useMediaQuery(useTheme().breakpoints.up("lg"));
  const [orientation, setOrientation] = useState<Orientation>(
    window.innerWidth > window.innerHeight ? "landscape" : "portrait",
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia("(orientation: portrait)");
    const setOrientationHandler = () => {
      setOrientation(mediaQueryList.matches ? "portrait" : "landscape");
    };

    mediaQueryList.addEventListener("change", setOrientationHandler);
    window.addEventListener("resize", setOrientationHandler);

    return () => {
      mediaQueryList.removeEventListener("change", setOrientationHandler);
      window.removeEventListener("resize", setOrientationHandler);
    };
  }, []);

  const getDeviceType = (viewportWidth: number): DeviceType => {
    if (viewportWidth >= 1280) {
      return "desktop";
    } else if (viewportWidth >= 600) {
      return "tablet";
    } else {
      return "phone";
    }
  };

  const deviceType = useMemo(() => {
    if (isDesktop) {
      return "desktop";
    }
    const deviceWidth: number = Math.min(window.innerWidth, window.innerHeight);
    const deviceType: DeviceType = getDeviceType(deviceWidth);
    return deviceType;
  }, []);

  return {
    type: deviceType,
    orientation,
  };
};

export default useDeviceType;
